import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { Download, ExternalLink, Paperclip, X } from "lucide-react";
import {
  PurchaseDocDetail,
  PurchaseDocFile,
  PurchaseDocFileState,
  PurchaseDocPerson,
  PurchaseDocSignature,
} from "types/InvoiceInbox";
import {
  addPurchaseDocFile,
  purchaseDocContentUrl,
  purchaseDocFileContentUrl,
  removePurchaseDocFile,
} from "services/invoice-inbox.service";
import { formatPrettyDate } from "utils/helperFunctions";
import useIsPhone from "features/files/use-is-phone";
import { canPreviewInline, hasPrimaryFile, isImage } from "./invoice-doc-preview";
import { fileSignState } from "./invoice-doc-signing";
import { buttonClass } from "./invoice-doc-ui";

/**
 * The files of one document, and the one being looked at.
 *
 * A purchase document is not always one PDF: an invoice split over several
 * files, the invoice plus its work act, a signed copy. The document's own
 * `file` is the PRIMARY one (first chip); `files[]` are the rest. Picking a
 * chip only changes what the preview shows — the reading on the right is still
 * the primary file's.
 */

type Props = {
  companyId: string;
  doc: PurchaseDocDetail["doc"];
  files: PurchaseDocFile[];
  people: Record<string, PurchaseDocPerson>;
  /** Signature requests and per-file signing state, for the chips' badges.
   * Optional: without them only a signed copy (`kind: 'signed'`) says so. */
  signatures?: PurchaseDocSignature[];
  fileStates?: Record<string, PurchaseDocFileState>;
  /** Reload the document after a file was added or taken out. */
  onChanged: () => Promise<unknown> | void;
};

type Entry = {
  id: string;
  name: string;
  fileName: string | null;
  mimeType: string | null;
  byteSize: number | null;
  signed: boolean;
  /** Somebody has been asked to sign it and not everybody has answered. */
  signing: boolean;
  removable: boolean;
  available: boolean;
  viewUrl: string;
  downloadUrl: string;
  title: string;
};

const PRIMARY = "primary";

/** What the "Add file" picker offers — the same types the intake accepts. */
export const ADD_FILE_ACCEPT = "application/pdf,image/jpeg,image/png,image/tiff,.pdf,.jpg,.jpeg,.png,.tif,.tiff";

/** "840 B", "12 KB", "1,4 MB" — the size next to a file name, nothing more precise. */
export function formatFileSize(bytes: number | null | undefined, locale = "lt-LT"): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(kb / 1024)} MB`;
}

/**
 * A refusal the server names by code, in the reader's language — the same
 * `invoice_inbox_refusal_<code>` idiom the panel's `run()` uses.
 *
 * Tried in order: `invoice_inbox_refusal_<code>` (most refusals, and the
 * attachment filter's own reason IS the `code` — see docs/add-file.js), then
 * `invoice_inbox_refusal_<reason>` for a refusal that wraps a specific reason
 * under a generic code (e.g. SEPARATION_OF_DUTIES carries `reason:
 * 'same_person_both_slots'`), then the server's own text. A code/reason we
 * have no words for at all falls back to the generic ATTACHMENT_REFUSED-style
 * message.
 */
export function refusalMessage(
  t: (key: string, options?: any) => string,
  exists: (key: string) => boolean,
  err: any
): string {
  const data = err && err.response && err.response.data;
  const codeKey = data && data.code ? `invoice_inbox_refusal_${data.code}` : "";
  const reasonKey = data && data.reason ? `invoice_inbox_refusal_${data.reason}` : "";
  return (
    (codeKey && exists(codeKey) ? t(codeKey) : "") ||
    (reasonKey && exists(reasonKey) ? t(reasonKey) : "") ||
    (data && (data.message || data.code)) ||
    t("invoice_inbox_action_failed", { defaultValue: "The action did not go through." })
  );
}

export default function InvoiceDocFiles({
  companyId,
  doc,
  files,
  people,
  signatures,
  fileStates,
  onChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<string>(PRIMARY);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  // On a phone the embedded viewer stays folded until asked for: iOS Safari
  // shows only the first page of a PDF in a frame and the frame takes over the
  // scroll, so the phone's own viewer ("open separately") is the better way to read it.
  const isPhone = useIsPhone();
  const [shownOnPhone, setShownOnPhone] = useState(false);
  const exists = (key: string) => Boolean((i18n as any).exists && (i18n as any).exists(key));
  const readOnly = Boolean(doc.deletedAt);

  const primaryUrl = purchaseDocContentUrl(companyId, doc.id);
  const primaryName = doc.fileName || t("invoice_inbox_file_primary", { defaultValue: "Document" });
  const entries: Entry[] = [
    {
      id: PRIMARY,
      name: primaryName,
      fileName: doc.fileName,
      mimeType: doc.mimeType,
      byteSize: null,
      signed: fileSignState(PRIMARY, signatures, fileStates) === "signed",
      signing: fileSignState(PRIMARY, signatures, fileStates) === "pending",
      removable: false,
      available: hasPrimaryFile(doc),
      viewUrl: primaryUrl,
      downloadUrl: `${primaryUrl}?download=1`,
      title: primaryName,
    },
    ...files.map((file) => {
      const url = purchaseDocFileContentUrl(companyId, doc.id, file.id);
      const name = file.fileName || t("invoice_inbox_file_primary", { defaultValue: "Document" });
      const uploader = file.uploadedBy && people[file.uploadedBy] ? people[file.uploadedBy].name : null;
      const date = formatPrettyDate(file.createdAt, "lt-LT", true);
      const signState = fileSignState(file.id, signatures, fileStates);
      const signed = file.kind === "signed" || signState === "signed";
      return {
        id: file.id,
        name,
        fileName: file.fileName,
        mimeType: file.mimeType,
        byteSize: file.byteSize,
        signed,
        signing: !signed && signState === "pending",
        // The primary file is the document itself and a signed copy is
        // evidence — neither is taken out from here (the server refuses the
        // latter with FILE_IS_SIGNED anyway). Nor is a file out for
        // signing: the people asked would be left with nothing to sign.
        removable: !readOnly && !signed && signState !== "pending",
        available: true,
        viewUrl: url,
        downloadUrl: `${url}?download=1`,
        title: uploader
          ? t("invoice_inbox_file_uploaded_by", {
              defaultValue: "Added by {{name}}, {{date}}",
              name: uploader,
              date,
            })
          : date,
      };
    }),
  ];
  // A file taken out (here or by somebody else) falls back to the primary.
  const current = entries.find((entry) => entry.id === selected) || entries[0];

  const onPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    // Cleared straight away so picking the same file again still fires a change.
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const result = await addPurchaseDocFile(companyId, doc.id, file);
      toast.success(t("invoice_inbox_file_added", { defaultValue: "File added" }));
      if (result && result.file && result.file.id) setSelected(result.file.id);
      await onChanged();
    } catch (err) {
      toast.error(refusalMessage(t, exists, err));
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async (entry: Entry) => {
    if (
      !window.confirm(
        t("invoice_inbox_file_remove_confirm", {
          defaultValue: "Remove the file {{name}} from this document?",
          name: entry.name,
        })
      )
    ) {
      return;
    }
    setRemoving(entry.id);
    try {
      await removePurchaseDocFile(companyId, doc.id, entry.id);
      toast.success(t("invoice_inbox_file_removed", { defaultValue: "File removed" }));
      if (selected === entry.id) setSelected(PRIMARY);
      await onChanged();
    } catch (err) {
      toast.error(refusalMessage(t, exists, err));
    } finally {
      setRemoving(null);
    }
  };

  const downloadLabel = t("invoice_inbox_file_download", { defaultValue: "Download" });

  return (
    <div className="flex flex-col">
      <div
        role="group"
        aria-label={t("invoice_inbox_files", { defaultValue: "Files" })}
        className="flex flex-wrap items-center gap-2 border-b border-gray-200 p-2"
      >
        {entries.map((entry) => {
          const active = entry.id === current.id;
          return (
            <div
              key={entry.id}
              data-testid={`invoice-doc-file-${entry.id}`}
              className={clsx(
                "flex max-w-full items-center rounded-md border text-sm",
                active ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white"
              )}
            >
              <button
                type="button"
                title={entry.title}
                aria-pressed={active}
                onClick={() => setSelected(entry.id)}
                className="flex min-w-0 items-center gap-1.5 rounded-l-md px-2 py-1 text-left hover:bg-gray-50 max-md:py-2.5"
              >
                <span className={clsx("truncate", active ? "font-medium text-blue-900" : "text-gray-800")}>
                  {entry.name}
                </span>
                {entry.byteSize !== null && (
                  <span className="shrink-0 text-xs text-gray-500">{formatFileSize(entry.byteSize)}</span>
                )}
                {entry.signed && (
                  <span className="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">
                    {t("invoice_inbox_file_signed", { defaultValue: "Signed" })}
                  </span>
                )}
                {entry.signing && (
                  <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                    {t("invoice_inbox_sign_state_pending", { defaultValue: "Signing in progress" })}
                  </span>
                )}
              </button>
              {entry.available && (
                // A new tab rather than a same-tab link: `?download=1` makes it
                // an attachment, so the tab downloads and closes — the panel
                // stays where it was.
                <a
                  href={entry.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={downloadLabel}
                  aria-label={`${downloadLabel}: ${entry.name}`}
                  className="p-1 text-gray-500 hover:text-gray-900 max-md:p-3"
                >
                  <Download className="h-4 w-4" />
                </a>
              )}
              {entry.removable && (
                <button
                  type="button"
                  disabled={removing === entry.id}
                  onClick={() => onRemove(entry)}
                  title={t("invoice_inbox_file_remove", { defaultValue: "Remove file" })}
                  aria-label={`${t("invoice_inbox_file_remove", { defaultValue: "Remove file" })}: ${entry.name}`}
                  className="rounded-r-md p-1 text-gray-500 hover:text-red-700 disabled:opacity-50 max-md:p-3"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}
        {!readOnly && (
          <>
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current && inputRef.current.click()}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-gray-400 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 max-md:min-h-11"
            >
              <Paperclip className="h-4 w-4" />
              {t("invoice_inbox_file_add", { defaultValue: "Add file" })}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept={ADD_FILE_ACCEPT}
              className="hidden"
              data-testid="invoice-doc-file-input"
              aria-label={t("invoice_inbox_file_add", { defaultValue: "Add file" })}
              onChange={onPick}
            />
          </>
        )}
      </div>

      {isPhone && current.available && canPreviewInline(current) && (
        <div className="flex flex-wrap gap-2 p-2">
          <a href={current.viewUrl} target="_blank" rel="noopener noreferrer" className={buttonClass}>
            <ExternalLink className="h-4 w-4" aria-hidden />
            {t("invoice_inbox_preview_open_file", { defaultValue: "Open the file separately" })}
          </a>
          <button
            type="button"
            className={buttonClass}
            aria-expanded={shownOnPhone}
            onClick={() => setShownOnPhone(!shownOnPhone)}
          >
            {shownOnPhone
              ? t("hide", { defaultValue: "Hide" })
              : t("invoice_inbox_preview", { defaultValue: "Preview the file" })}
          </button>
        </div>
      )}

      {!current.available ? (
        <div className="p-6 text-gray-500">
          {t("invoice_inbox_file_pending", {
            defaultValue: "The file is still being filed away — reload in a moment.",
          })}
        </div>
      ) : !canPreviewInline(current) ? (
        // A .tiff or an .xml is a real document — it just cannot be shown here.
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm text-gray-600">
            {t("invoice_inbox_file_cannot_preview", {
              defaultValue: "This file cannot be shown here — download it.",
            })}
          </p>
          <a
            href={current.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-700"
          >
            <Download className="h-4 w-4" />
            {downloadLabel}
          </a>
        </div>
      ) : isPhone && !shownOnPhone ? null : isImage(current) ? (
        <div className="h-[75dvh] overflow-auto p-2 text-center">
          <img
            src={current.viewUrl}
            alt={current.name}
            data-testid="invoice-doc-file-preview"
            className="mx-auto max-w-full"
          />
        </div>
      ) : (
        <iframe
          title={current.name}
          src={current.viewUrl}
          data-testid="invoice-doc-file-preview"
          className="h-[75dvh] w-full border-0"
        />
      )}
    </div>
  );
}
